import { RichTextRenderer } from "@scott/contentful/rich-text-renderer";
import type {
  ContentfulButton,
  ContentfulHeadlineBlock,
} from "@scott/contentful/types";
import { cva } from "class-variance-authority";
import type { ReactNode } from "react";
import { Button } from "../../elements/button/button";
import { TextD2C } from "../../elements/text-d2c/text";
import { IconChevronRight } from "../../icons/chevron-right-icon";
import { Column, Container, Row } from "../../layout/grid";
import {
  ctaAttributes,
  type PromotionTracking,
  promotionAttributes,
} from "../../lib/promotion-tracking";
import { cn } from "../../utils";
import { VideoBlock } from "../video/video";
import type { HeroSectionInspectorProps } from "./hero-section.preview";

const contentSizeVariants = cva("", {
  variants: {
    contentSize: {
      viewport:
        "h-[max(0px,calc(100dvh_-_var(--hero-viewport-inset,var(--site-header-height,0px))))] w-full",
      "grid-16-9": "aspect-1/2 md:aspect-video",
      "grid-21-9": "aspect-1/2 md:aspect-21/9",
    },
  },
  defaultVariants: {
    contentSize: "viewport",
  },
});

const textPositionVariants = cva("", {
  variants: {
    vertical: {
      top: "justify-start",
      center: "justify-center",
      bottom: "justify-end",
    },
    horizontal: {
      left: "justify-start",
      center: "justify-center text-center",
    },
  },
  defaultVariants: {
    vertical: "center",
    horizontal: "center",
  },
});

const backgroundColorVariants = cva("", {
  variants: {
    backgroundColor: {
      black: "bg-black",
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
    },
  },
  defaultVariants: {
    backgroundColor: "white",
  },
});

const buttonsPositionVariants = cva("", {
  variants: {
    buttonsPosition: {
      left: "justify-start",
      center: "justify-center",
      right: "justify-end",
    },
  },
  defaultVariants: {
    buttonsPosition: "left",
  },
});

const headlineStyleBySize = {
  small: "hero-section-small",
  medium: "hero-section",
  large: "hero-section-large",
} as const;

export interface HeroSectionProps {
  sys?: { id: string };
  /** Analytics identity; enables view_promotion/select_promotion tracking. */
  promotion?: PromotionTracking;
  /** 1-based position when the hero is a slide of a slider. */
  promotionPosition?: number;
  contentSize?: "viewport" | "grid-21-9" | "grid-16-9";
  textVerticalPosition?: "top" | "center" | "bottom";
  /** Desktop headline font size: small 64px, medium 100px, large 142px. */
  headlineSize?: "small" | "medium" | "large";
  textHorizontalPosition?: "left" | "center";
  color?:
    | "black"
    | "white"
    | "alabastergrey"
    | "blazeorange"
    | "neonchartreuse";
  backgroundColor?: "black" | "white" | "whitesmoke";
  buttonsPosition?: "left" | "center" | "right";
  media:
    | Array<{
        sys?: { id: string };
        src: string;
        srcSet?: string;
        alt: string;
        mobileSrc?: string;
        mobileSrcSet?: string;
        type?: "Image" | "Video";
      }>
    | {
        sys?: { id: string };
        src: string;
        poster?: string;
        type?: "Image" | "Video";
      };
  title?: ContentfulHeadlineBlock;
  svg?: {
    sys?: { id: string };
    src?: string;
    alt?: string;
  };
  buttons?: ContentfulButton[];
  /**
   * Gives the primary hero image eager, high-priority loading. Disable this
   * for off-screen carousel slides so they do not compete with the LCP image.
   * @default true
   */
  mediaPriority?: boolean;
  /** Mounts video playback only while this hero is active in a slider. */
  mediaActive?: boolean;
  /** Shows an accessible play/pause control for background video. */
  showVideoPlaybackControl?: boolean;
  /** Accessible label for playing a background-video hero. */
  playVideoText?: string;
  /** Accessible label for pausing a background-video hero. */
  pauseVideoText?: string;
  inspectorProps?: HeroSectionInspectorProps;
}

export function HeroSection(props: Readonly<HeroSectionProps>) {
  const containerClasses = backgroundColorVariants({
    backgroundColor: props.backgroundColor,
  });

  const hero = (
    <Container
      className={cn(containerClasses, "relative", "component-hero-section")}
      fullWidth={props.contentSize === "viewport"}
      outerContainerOnly={true}
      paddingBottom={"none"}
      paddingTop={"none"}
      {...props.inspectorProps?.container}
    >
      <HeroAsset {...props} />
      <HeroContent {...props} />
    </Container>
  );
  const promotionProps = promotionAttributes(
    props.promotion,
    "hero",
    props.promotionPosition
  );

  return promotionProps ? <div {...promotionProps}>{hero}</div> : hero;
}

function HeroAsset(props: Readonly<HeroSectionProps>) {
  const mediaClasses = contentSizeVariants({
    contentSize: props.contentSize,
  });
  const mediaPriority = props.mediaPriority ?? true;

  // media type: image(s)
  if (Array.isArray(props.media)) {
    const hasTwo = props.media.length > 1;
    const columnClasses = hasTwo ? "md:grid-cols-2 grid-cols-1" : "grid-cols-1";
    // Primary image fills the row alone or half of it (two-column layout).
    const primarySizes = hasTwo ? "(min-width: 768px) 50vw, 100vw" : "100vw";

    return (
      <div
        className={cn(
          columnClasses,
          mediaClasses,
          "grid overflow-hidden",
          "hero-section__image"
        )}
      >
        {/* main image — the LCP element: eager + high priority */}
        {props.media[0] && (
          <picture className={cn("overflow-hidden")}>
            {(props.media[0].mobileSrcSet || props.media[0].mobileSrc) && (
              <source
                media="(max-width: 767px), (orientation: portrait)"
                sizes="100vw"
                srcSet={props.media[0].mobileSrcSet || props.media[0].mobileSrc}
              />
            )}
            <img
              alt={props.media[0].alt}
              className={cn("h-full w-full object-cover object-center")}
              fetchPriority={mediaPriority ? "high" : undefined}
              loading={mediaPriority ? "eager" : "lazy"}
              sizes={props.media[0].srcSet ? primarySizes : undefined}
              src={props.media[0].src}
              srcSet={props.media[0].srcSet}
            />
          </picture>
        )}

        {/* second image (Desktop only, half width) */}
        {props.media[1] && (
          <picture className={cn("hidden overflow-hidden md:block")}>
            {(props.media[1].mobileSrcSet || props.media[1].mobileSrc) && (
              <source
                media="(max-width: 767px), (orientation: portrait)"
                sizes="100vw"
                srcSet={props.media[1].mobileSrcSet || props.media[1].mobileSrc}
              />
            )}
            <img
              alt={props.media[1].alt}
              className={cn("h-full w-full object-cover object-center")}
              loading={mediaPriority ? "eager" : "lazy"}
              sizes={props.media[1].srcSet ? "50vw" : undefined}
              src={props.media[1].src}
              srcSet={props.media[1].srcSet}
            />
          </picture>
        )}
      </div>
    );
  }

  // media type: video
  if (!Array.isArray(props.media) && props.media?.type === "Video") {
    const mediaActive = props.mediaActive ?? true;

    return (
      <div
        className={cn(
          mediaClasses,
          "grid grid-cols-1 overflow-hidden",
          "hero-section__video relative"
        )}
      >
        {mediaActive ? (
          <VideoBlock
            background={props.backgroundColor}
            className="hero-section__video-player"
            fullWidth
            isBackgroundVideo
            paddingBottom="none"
            paddingTop="none"
            pauseBackgroundVideoText={props.pauseVideoText}
            playBackgroundVideoText={props.playVideoText}
            poster={props.media.poster}
            showBackgroundPlaybackControl={props.showVideoPlaybackControl}
            src={props.media.src}
          />
        ) : (
          props.media.poster && (
            <img
              alt=""
              aria-hidden="true"
              className="h-full w-full object-cover object-center"
              loading="lazy"
              src={props.media.poster}
            />
          )
        )}
      </div>
    );
  }
}

function HeroContent(props: Readonly<HeroSectionProps>) {
  const hasButtons = Boolean(props.buttons && props.buttons.length > 0);

  return (
    <div className={"container--fluid absolute inset-0"}>
      <Row
        className={cn(
          "h-full",
          hasButtons ? "grid-rows-[1fr_auto]" : "",
          props.contentSize === "viewport" ? "" : "px-small"
        )}
      >
        <HeroText {...props} />
        <HeroButtons {...props} />
      </Row>
    </div>
  );
}

function HeroText(props: Readonly<HeroSectionProps>) {
  const columnClasses =
    props.textHorizontalPosition === "center"
      ? "col-span-12"
      : "col-span-10 col-start-2 md:col-span-8 md:col-start-3";

  const textVerticalPositionClass = textPositionVariants({
    vertical: props.textVerticalPosition,
    horizontal: null,
  });

  const textHorizontalPositionClass = textPositionVariants({
    vertical: null,
    horizontal: props.textHorizontalPosition,
  });

  const content = (): ReactNode => {
    if (props.svg) {
      return (
        <img
          alt={props.svg?.alt}
          className={cn(
            props.textHorizontalPosition === "center" ? "m-auto" : ""
          )}
          src={props.svg?.src}
          {...props.inspectorProps?.svg}
        />
      );
    }

    if (props.title?.headline) {
      return (
        <>
          {props.title?.eyebrow && (
            <TextD2C
              color={props.color}
              variant="pillar"
              {...props.inspectorProps?.eyebrow}
            >
              {props.title.eyebrow}
            </TextD2C>
          )}

          <TextD2C
            color={props.color}
            style={headlineStyleBySize[props.headlineSize ?? "medium"]}
            variant="h1"
            {...props.inspectorProps?.title}
          >
            <RichTextRenderer richText={props.title.headline.json} />
          </TextD2C>

          {props.title?.subline && (
            <TextD2C
              color={props.color}
              style="hero-section-subline"
              variant="h3"
              {...props.inspectorProps?.subline}
            >
              {props.title.subline}
            </TextD2C>
          )}
        </>
      );
    }

    return null;
  };

  return (
    <Column className={cn(columnClasses)}>
      <div className="flex h-full flex-col">
        <div
          className={cn(
            "flex h-full flex-col",
            props.textVerticalPosition === "top"
              ? "pt-[calc(var(--spacing-small)+var(--hero-content-top-inset,0px))]"
              : "pt-small",
            textVerticalPositionClass
          )}
        >
          <div className={cn(textHorizontalPositionClass)}>{content()}</div>
        </div>
      </div>
    </Column>
  );
}

function HeroButtons(props: Readonly<HeroSectionProps>) {
  const buttons = props.buttons?.slice(0, 2);
  const hasButtons = Boolean(buttons && buttons.length > 0);

  if (!hasButtons) {
    return null;
  }

  const buttonsPositionClass = buttonsPositionVariants({
    buttonsPosition: props.buttonsPosition,
  });

  return (
    <Column className="col-span-12">
      <div
        className={cn(
          "pointer-events-auto flex flex-row flex-wrap gap-4 pb-small",
          buttonsPositionClass
        )}
      >
        {buttons?.map((btn, index) => {
          const inspectorKey = `button${index}` as "button0" | "button1";
          const inspector = props.inspectorProps?.[inspectorKey];
          return inspector ? (
            <span key={`${btn.href}-${btn.label}`} {...inspector}>
              <Button
                className="font-brand"
                href={btn.href}
                size="lg"
                target={btn.target}
                type="button"
                variant={btn.variant ?? "default"}
                {...ctaAttributes(btn)}
              >
                {btn.label}
                <IconChevronRight />
              </Button>
            </span>
          ) : (
            <Button
              className="font-brand"
              href={btn.href}
              key={`${btn.href}-${btn.label}`}
              mode="D2C"
              size="lg"
              target={btn.target}
              type="button"
              variant={btn.variant ?? "default"}
              {...ctaAttributes(btn)}
            >
              {btn.label}
              <IconChevronRight />
            </Button>
          );
        })}
      </div>
    </Column>
  );
}
